import { Formik, Form, Field, ErrorMessage, FormikHelpers } from "formik";
import { useAppDispatch, useAppSelector } from "store/hooks/hooks";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { Link } from "react-router-dom";
import AuthLayout from "components/AuthLayout/AuthLayout";
import { useState, useEffect, useCallback, useRef } from "react";
import { authPageMeta } from "data/authPages.enum";
import { ILoginPayload } from "Interfaces/interface.auth";
import {
  loginUser,
  socialLogin,
  selectAuthLoading,
  selectIsAuthenticated,
} from "store/slices/authSlice";
import { clearCookies } from "services/cookieService";
import { handle406LoginError } from "utils/authHandlers";
import { EMAIL_PATTERN } from "utils/validationRegex";
import LucideIcon from "utils/LucideIcon";
import { environment } from "../../environments/environment";

declare global {
  interface Window {
    google: any;
  }
}

const validateForm = (values: ILoginPayload) => {
  const errors: Partial<ILoginPayload> = {};
  if (!values.username) {
    errors.username = "Email is required";
  } else if (!EMAIL_PATTERN.test(values.username)) {
    errors.username = "Please enter a valid email address";
  }
  if (!values.password) {
    errors.password = "Password is required";
  }
  return errors;
};

const PageLogin = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [passwordType, setPasswordType] = useState("password");
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const loading = useAppSelector(selectAuthLoading);
  const googleButtonRef = useRef<HTMLDivElement>(null);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Handle Google Sign-In callback
  const handleGoogleCallback = useCallback(
    async (response: any) => {
      try {
        if (response.credential) {
          clearCookies();
          const result = await dispatch(
            socialLogin({ accesstoken: response.credential })
          ).unwrap();
          navigate("/", { replace: true });
        } else {
          throw new Error("No credential received");
        }
      } catch (err: any) {
        console.error("Google login error:", err);
        toast.error(err || "Google login failed. Please try again.");
      }
    },
    [dispatch, navigate]
  );

  // Initialize Google Sign-In
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    const initializeGoogleSignIn = () => {
      if (window.google?.accounts?.id && googleButtonRef.current) {
        if (!environment.GOOGLE_CLIENT_ID) {
          console.error("Google Client ID is not defined in .env");
          return;
        }

        window.google.accounts.id.initialize({
          client_id: environment.GOOGLE_CLIENT_ID,
          callback: handleGoogleCallback,
          itp_support: true,
          auto_select: true,
          cancel_on_tap_outside: false,
        });

        try {
          window.google.accounts.id.renderButton(googleButtonRef.current, {
            theme: "outline",
            size: "large",
            type: "icon",
            text: "signin_with",
            width: "240px",
          });
        } catch (error) {
          console.error("Failed to render Google Sign-In button:", error);
        }

        window.google.accounts.id.prompt((notification: any) => {
          console.log("Google prompt notification:", notification);
        });

        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      } else {
        console.warn("Google Identity Services or button ref not ready");
      }
    };

    // Start polling after a short delay to allow DOM mount
    const timer = setTimeout(() => {
      intervalId = setInterval(initializeGoogleSignIn, 500);
    }, 100);

    // Clean up
    return () => {
      if (intervalId) clearInterval(intervalId);
      clearTimeout(timer);
    };
  }, [handleGoogleCallback]);

  const handleLogin = async (
    values: ILoginPayload,
    { setSubmitting }: FormikHelpers<ILoginPayload>
  ) => {
    try {
      clearCookies();
      const result = await dispatch(loginUser(values)).unwrap();
      toast.success("Signed in successfully!");
      navigate("/", { replace: true });
    } catch (err: any) {
      const result = await handle406LoginError(err, values.username);
      if (result.shouldVerifyEmail) {
        navigate("/verify-email", { state: { email: values.username } });
      }

      setSubmitting(false);
    }
  };

  return (
    <AuthLayout {...authPageMeta.login}>
      <motion.div
        className="w-full flex justify-center items-center"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" as const, delay: 0.2 }}
      >
        <div className="bg-white dark:bg-neutral-800 rounded-2xl w-full max-w-md p-6">
          <h2 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100 mb-4 text-center font-display">
            Sign In
          </h2>
          <Formik
            initialValues={{ username: "", password: "" }}
            validate={validateForm}
            onSubmit={handleLogin}
          >
            {({ isSubmitting }) => (
              <Form className="space-y-3">
                <div>
                  <label className="block text-neutral-800 dark:text-neutral-200 font-medium font-body text-xs">
                    Email Address
                  </label>
                  <Field
                    id="username"
                    name="username"
                    type="email"
                    placeholder="name@company.com"
                    className="mt-1 block w-full rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-sm font-normal h-10 px-3 py-2 focus:ring-2 focus:ring-primary-300 focus:border-primary-300 dark:focus:ring-primary-600 transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={isSubmitting || loading}
                  />
                  <ErrorMessage
                    name="username"
                    component="div"
                    className="text-red-500 text-xs mt-1"
                  />
                </div>
                <div>
                  <label className="block text-neutral-800 dark:text-neutral-200 font-medium font-body text-xs">
                    Password
                  </label>
                  <div className="relative">
                    <Field
                      type={passwordType}
                      id="password"
                      name="password"
                      placeholder="Password"
                      className="mt-1 block w-full rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-sm font-normal h-10 px-3 py-2 focus:ring-2 focus:ring-primary-300 focus:border-primary-300 dark:focus:ring-primary-600 transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={isSubmitting || loading}
                    />
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-primary-500 hover:text-primary-600 text-xs font-body">
                      {passwordType === "password" ? (
                        <LucideIcon
                          name="EyeOff"
                          className="p-1 cursor-pointer"
                          onClick={() => setPasswordType("text")}
                        />
                      ) : (
                        <LucideIcon
                          name="Eye"
                          className="p-1 cursor-pointer"
                          onClick={() => setPasswordType("password")}
                        />
                      )}
                    </div>
                  </div>
                  <ErrorMessage
                    name="password"
                    component="div"
                    className="text-red-500 text-xs mt-1"
                  />
                </div>
                <motion.div className="w-full flex flex-col gap-2 justify-between items-center">
                  <ButtonPrimary
                    type="submit"
                    disabled={isSubmitting || loading}
                    loading={isSubmitting || loading}
                    className="text-white font-semibold py-2 rounded-xl shadow-md transition-colors duration-300 hover:scale-105 ease-in text-sm disabled:cursor-not-allowed disabled:text-gray-800"
                    sizeClass="w-full"
                  >
                    {!isSubmitting && !loading && "Sign In"}
                  </ButtonPrimary>
                  <Link
                    to={isSubmitting || loading ? "#" : "/forgot-password"}
                    onClick={(e) => {
                      if (isSubmitting || loading) e.preventDefault();
                    }}
                    className={`text-primary-500 hover:text-primary-600 text-xs font-body ml-3 
    ${isSubmitting || loading ? "pointer-events-none opacity-50" : ""}`}
                  >
                    Forgot password?
                  </Link>
                </motion.div>
                <div className="mt-3">
                  <div className="relative text-center">
                    <span className="relative z-10 inline-block px-3 font-medium text-xs bg-white dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400">
                      Or sign in with
                    </span>
                    <div className="absolute left-0 w-full top-1/2 transform -translate-y-1/2 border border-neutral-200 dark:border-neutral-700"></div>
                  </div>
                  <div className="flex justify-center gap-3 mt-3">
                    <div
                      id="google-button"
                      ref={googleButtonRef}
                      className={`google-signin-button min-w-[240px] min-h-[40px] flex items-center justify-center 
      ${isSubmitting || loading ? "pointer-events-none opacity-50" : ""}`}
                    >
                      <span className="text-neutral-500">
                        Loading Google Sign-In...
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mt-3 text-center">
                  <span className="text-neutral-700 dark:text-neutral-300 font-body text-sm">
                    New here?{" "}
                    <Link
                      to={isSubmitting || loading ? "#" : "/signup"}
                      onClick={(e) => {
                        if (isSubmitting || loading) e.preventDefault();
                      }}
                      className={`text-primary-500 hover:text-primary-600 font-medium hover:underline transition-colors duration-300 
        ${isSubmitting || loading ? "pointer-events-none opacity-50" : ""}`}
                    >
                      Sign Up
                    </Link>
                  </span>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </motion.div>
    </AuthLayout>
  );
};

export default PageLogin;
